import { Button } from '@mui/material';
import type { ConferenceFilter, SiteResponse, SportFilter } from '@on3/api';
import {
  groupFiveConferencesIds,
  powerFiveConferencesIds,
} from '@on3/ui-lib/utils/constants';
import clsx from 'clsx';
import { Text } from 'components/UI/Typography/Text';
import { useMemo, useState } from 'react';

import styles from './Colleges.module.scss';
import { TeamItem } from './TeamItem';
import { TeamMiniNav } from './TeamMiniNav';

export interface ICollegeProps {
  conferences: ConferenceFilter[];
  sites: SiteResponse[] | null;
  filters?: {
    sports: SportFilter[];
    years: number[];
  };
}
export const Colleges = ({ conferences, sites }: ICollegeProps) => {
  const channelSites = sites?.filter((site: SiteResponse) => site?.isChannel);

  const powerFiveConferences = (conferences || []).filter((conference) =>
    powerFiveConferencesIds.includes(conference?.key || 0),
  );

  const groupFiveConferences = (conferences || []).filter((conference) =>
    groupFiveConferencesIds.includes(conference?.key || 0),
  );

  const displayConferences = [...powerFiveConferences, ...groupFiveConferences];
  const [selectedConference, setSelectedConference] = useState('All');

  const filteredTeams = useMemo(() => {
    const allowedConferencesIds = [
      ...powerFiveConferencesIds,
      ...groupFiveConferencesIds,
    ];

    let teams = channelSites || [];

    if (selectedConference !== 'All') {
      teams = teams.filter(
        (site) => site.conference?.abbreviation === selectedConference,
      );
    }

    return teams
      .filter((team) =>
        allowedConferencesIds.includes(team?.conference?.key || 0),
      )
      .sort((a, b) => {
        const first = a?.organization?.name?.toLowerCase();
        const second = b?.organization?.name?.toLowerCase();

        return first && second ? first.localeCompare(second) : 0;
      });
  }, [channelSites, selectedConference]);

  const handleClick = (conference: string | null) => {
    setSelectedConference(conference || 'All');
  };

  const isAllConference = selectedConference === 'All';

  return (
    <section>
      <TeamMiniNav />
      <div className={styles.nav}>
        <Button
          className={clsx(styles.button, isAllConference && styles.active)}
          onClick={() => handleClick('All')}
        >
          <Text
            className={clsx(styles.team, {
              [styles.active]: isAllConference,
            })}
            component="span"
          >
            All
          </Text>
        </Button>
        {displayConferences.map(({ key, abbreviation }) => {
          const isSelectedConference = selectedConference === abbreviation;

          return (
            <Button
              className={clsx(
                styles.button,
                isSelectedConference && styles.active,
              )}
              key={key}
              onClick={() => handleClick(abbreviation || '')}
            >
              <Text
                className={clsx(styles.team, {
                  [styles.active]: isSelectedConference,
                })}
                component="span"
              >
                {abbreviation}
              </Text>
            </Button>
          );
        })}
      </div>
      <ul className={styles.teamWrapper}>
        {filteredTeams.map((site: SiteResponse) => {
          if (!site?.organization) {
            return null;
          }

          const siblingSite = sites?.find((s) => s.key === site.siblingSiteKey);

          return (
            <TeamItem key={site?.key} siblingSite={siblingSite} site={site} />
          );
        })}
      </ul>
    </section>
  );
};
